import { useRef, useState } from 'react';
import { useQuery } from 'react-query';
import CsvDownload from 'react-json-to-csv';
import * as api from 'actions';
import { UIChart, UiFilter, Button, NoRecord } from 'components/generic';
import { Images } from 'assets/Images';
import { buildHeaderMeta, buildRuleCondition, aggregateReportData, buildDownloadRows } from 'helpers/reportAggregation';
import { isFailedResponse } from 'helpers/apiResponse';
import './style.scss';

const emptyReportReqObj = { ruleCondition: [], filter: [], sort: [], timezone: 'Asia/Kolkata', download: 'N', preview: 'N', record_count: 10000, start_index: 0 };

export default function ChartCard({ report, entity, product, team, branch }) {
    const [tableHeader, setTableHeader] = useState([]);
    const [chartDropdown, setChartDropdown] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [downloadData, setDownloadData] = useState([]);
    const [showFilter, setShowFilter] = useState(false);
    const [filterObj, setFilterObj] = useState([]);
    const [hasError, setHasError] = useState(false);
    const [hasHeader, setHasHeader] = useState(false);

    const levelsRef = useRef({});
    const reportReqObjRef = useRef({ ...emptyReportReqObj });

    const { isLoading: headerLoading, isError: headerThrew, refetch: refetchHeader } = useQuery(
        ['reportHeader', report.reportId],
        () => api.get(`config/${report.reportId}`),
        {
            refetchOnWindowFocus: false,
            onSuccess: (res) => {
                if (isFailedResponse(res)) { setHasError(true); return; }
                const header = !res?.data?.data?.length ? [] : res.data.data[0];
                if (!header.rc_columnsList?.length) { setHasError(true); return; }

                const meta = buildHeaderMeta(header.rc_columnsList);
                levelsRef.current = meta;
                setTableHeader(meta.tableHeader);
                setChartDropdown(meta.chartDropdown);

                const ruleCondition = buildRuleCondition(header.rc_variables || [], {
                    level1: meta.level1, level2: meta.level2, entity, product, team, branch,
                });
                const defaultFilter = header.rc_defaultFilter?.map(el => ({ id: el.id, value: el.value })) || [];
                reportReqObjRef.current = { ...emptyReportReqObj, ruleCondition, filter: defaultFilter };
                setFilterObj(defaultFilter);
                setHasError(false);
                setHasHeader(true);
                refetchData();
            },
        }
    );

    const { isLoading: dataLoading, isError: dataThrew, refetch: refetchData } = useQuery(
        ['reportData', report.reportId],
        () => api.post(report.reportId, reportReqObjRef.current),
        {
            refetchOnWindowFocus: false,
            enabled: false,
            onSuccess: (res) => {
                if (isFailedResponse(res)) { setHasError(true); return; }
                const rows = !res?.data?.data?.length ? [] : res.data.data[0]?.result || [];
                const aggregated = aggregateReportData(rows, {
                    level1: levelsRef.current.level1, level2: levelsRef.current.level2, percentObject: levelsRef.current.percentObject,
                });
                setChartData(aggregated);
                setDownloadData(buildDownloadRows(aggregated, levelsRef.current.tableHeader, {
                    level1: levelsRef.current.level1, level2: levelsRef.current.level2,
                }));
                setHasError(false);
            },
        }
    );

    const handleFilter = (state, obj) => {
        let nextFilter = [];
        if (state === 'save') {
            const cleaned = { ...obj };
            Object.keys(cleaned).filter(key => !cleaned[key]).forEach(key => delete cleaned[key]);
            nextFilter = Object.entries(cleaned).filter(([, value]) => value !== '').map(([id, value]) => ({ id, value }));
        }
        reportReqObjRef.current = { ...reportReqObjRef.current, filter: nextFilter };
        setFilterObj(nextFilter);
        refetchData();
    };

    const handleRetry = () => {
        setHasError(false);
        hasHeader ? refetchData() : refetchHeader();
    };

    const loading = headerLoading || dataLoading;
    const errored = hasError || headerThrew || dataThrew;
    const empty = !loading && !errored && (!chartDropdown.length || !chartData.length);
    const ready = !loading && !errored && !empty;

    return (
        <div className="chart-card">
            <div className="chart-card__title">
                <h3 title={report.reportName}>{report.reportName}</h3>
            </div>

            {ready && (
                <div className="chart-card__actions">
                    <Button compact transparent primary _src={Images.filter} label="Filter" handleOnSubmit={() => setShowFilter(v => !v)} />
                    <CsvDownload className="download-button" data={downloadData} filename={`${report.reportName}.csv`}>
                        <img src={Images.download} alt="" className="download-button__icon" />
                        Download
                    </CsvDownload>
                </div>
            )}

            {ready && showFilter && (
                <UiFilter tableHeader={tableHeader} defaultFilter={filterObj} handleSubmit={handleFilter} />
            )}

            <div className="chart-card__body">
                {loading && (
                    <div className="chart-card__spinner-wrap">
                        <span className="chart-card__spinner" />
                    </div>
                )}
                {!loading && errored && (
                    <div className="chart-card__error">
                        <p>Couldn't load this chart.</p>
                        <Button small primary label="Retry" handleOnSubmit={handleRetry} />
                    </div>
                )}
                {!loading && !errored && empty && <NoRecord description="No chart data for this report." />}
                {ready && (
                    <UIChart
                        summary={chartData}
                        chartDropdown={chartDropdown}
                        barKey={chartDropdown[0]?.key}
                        level1={levelsRef.current.level1}
                        level2={levelsRef.current.level2}
                    />
                )}
            </div>
        </div>
    );
}
